import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import {
  PageRecordType,
  releasePointerCapture,
  setPointerCapture,
  useEditor,
  useValue
} from "@tldraw/editor";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { PORTRAIT_BREAKPOINT } from "../../constants.mjs";
import { useBreakpoint } from "../../context/breakpoints.mjs";
import { useUiEvents } from "../../context/events.mjs";
import { useMenuIsOpen } from "../../hooks/useMenuIsOpen.mjs";
import { useReadonly } from "../../hooks/useReadonly.mjs";
import { useTranslation } from "../../hooks/useTranslation/useTranslation.mjs";
import { TldrawUiButton } from "../primitives/Button/TldrawUiButton.mjs";
import { TldrawUiButtonIcon } from "../primitives/Button/TldrawUiButtonIcon.mjs";
import { TldrawUiButtonLabel } from "../primitives/Button/TldrawUiButtonLabel.mjs";
import {
  TldrawUiPopover,
  TldrawUiPopoverContent,
  TldrawUiPopoverTrigger
} from "../primitives/TldrawUiPopover.mjs";
import { onMovePage } from "./edit-pages-shared.mjs";
import { PageItemInput } from "./PageItemInput.mjs";
import { PageItemSubmenu } from "./PageItemSubmenu.mjs";
const PAGE_MENU_LIST_HEIGHT_KEY = "tldraw_page_menu_list_height";
const MAX_PAGE_MENU_RENDER_HEIGHT = 800;
const LIST_BOTTOM_PADDING = 4;
const MAX_PAGE_MENU_AVAILABLE_HEIGHT_RATIO = 0.62;
const PAGE_MENU_CREATE_BUTTON_HEIGHT = 40;
const PAGE_MENU_RESIZE_HANDLE_HEIGHT = 1;
const PAGE_MENU_ITEM_HEIGHT = 36;
const MIN_PAGE_MENU_LIST_HEIGHT = PAGE_MENU_ITEM_HEIGHT + LIST_BOTTOM_PADDING;
const PAGE_MENU_DRAG_THRESHOLD = 5;
const PAGE_MENU_AUTO_SCROLL_ZONE = 16;
const PAGE_MENU_AUTO_SCROLL_RAMP_DISTANCE = 48;
const PAGE_MENU_MIN_AUTO_SCROLL_SPEED = 1;
const PAGE_MENU_MAX_AUTO_SCROLL_SPEED = 6;
function readSavedPageMenuListHeight() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(PAGE_MENU_LIST_HEIGHT_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n >= MIN_PAGE_MENU_LIST_HEIGHT ? n : null;
  } catch {
    return null;
  }
}
function getPageMenuRenderCap(availableHeight) {
  const maxMenuHeight = Math.min(
    MAX_PAGE_MENU_RENDER_HEIGHT,
    availableHeight * MAX_PAGE_MENU_AVAILABLE_HEIGHT_RATIO
  );
  const footerHeight = PAGE_MENU_CREATE_BUTTON_HEIGHT + PAGE_MENU_RESIZE_HANDLE_HEIGHT;
  return Math.max(MIN_PAGE_MENU_LIST_HEIGHT, maxMenuHeight - footerHeight);
}
function getPageMenuAutoFitListHeight(pageCount) {
  return Math.max(
    MIN_PAGE_MENU_LIST_HEIGHT,
    pageCount * PAGE_MENU_ITEM_HEIGHT + LIST_BOTTOM_PADDING
  );
}
const DefaultPageMenu = memo(function DefaultPageMenu2() {
  const editor = useEditor();
  const trackEvent = useUiEvents();
  const msg = useTranslation();
  const breakpoint = useBreakpoint();
  const [editingPageId, setEditingPageId] = useState(null);
  const closePageItemSubmenus = useCallback(
    (exceptIndex) => {
      const contextSuffix = `-${editor.contextId}`;
      for (const menuId of editor.menus.getOpenMenus()) {
        const id = menuId.endsWith(contextSuffix) ? menuId.slice(0, -contextSuffix.length) : menuId;
        if (!id.startsWith("page item submenu ")) continue;
        if (exceptIndex !== void 0 && id === `page item submenu ${exceptIndex}`) continue;
        editor.menus.deleteOpenMenu(id);
      }
    },
    [editor]
  );
  const handleOpenChange = useCallback(
    (isOpen2) => {
      setEditingPageId(null);
      if (!isOpen2) {
        closePageItemSubmenus();
      }
    },
    [closePageItemSubmenus]
  );
  const [isOpen, onOpenChange] = useMenuIsOpen("page-menu", handleOpenChange);
  const rSortableContainer = useRef(null);
  const pages = useValue("pages", () => editor.getPages(), [editor]);
  const currentPage = useValue("currentPage", () => editor.getCurrentPage(), [editor]);
  const isReadonlyMode = useReadonly();
  const isCoarsePointer = useValue(
    "isCoarsePointer",
    () => editor.getInstanceState().isCoarsePointer,
    [editor]
  );
  const [userListHeight, setUserListHeight] = useState(readSavedPageMenuListHeight);
  const [isResizing, setIsResizing] = useState(false);
  const [availableHeight, setAvailableHeight] = useState(
    () => typeof window === "undefined" ? 800 : window.innerHeight
  );
  const updateAvailableHeight = useCallback(() => {
    if (typeof window === "undefined") return;
    const popoverContent = rSortableContainer.current?.closest(".tlui-popover__content");
    const radixAvailableHeight = popoverContent ? Number.parseFloat(
      getComputedStyle(popoverContent).getPropertyValue(
        "--radix-popover-content-available-height"
      )
    ) : NaN;
    setAvailableHeight(
      Number.isFinite(radixAvailableHeight) ? radixAvailableHeight : window.innerHeight
    );
  }, []);
  useEffect(() => {
    const onResize = () => updateAvailableHeight();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [updateAvailableHeight]);
  useEffect(() => {
    if (!isOpen) return;
    editor.timers.requestAnimationFrame(updateAvailableHeight);
  }, [editor, isOpen, updateAvailableHeight]);
  const renderCap = getPageMenuRenderCap(availableHeight);
  const autoFitListHeight = getPageMenuAutoFitListHeight(pages.length);
  const renderedListHeight = Math.min(userListHeight ?? autoFitListHeight, renderCap);
  const hasReachedMaxPages = pages.length >= editor.options.maxPages;
  const createPageButtonLabel = msg(
    hasReachedMaxPages ? "page-menu.max-pages-reached" : "page-menu.create-new-page"
  );
  const handleResizePointerDown = useCallback((e) => {
    e.preventDefault();
    const handle = e.currentTarget;
    handle.setPointerCapture(e.pointerId);
    const startY = e.clientY;
    const startHeight = handle.previousElementSibling?.getBoundingClientRect().height ?? 0;
    let nextHeight = startHeight;
    setIsResizing(true);
    const onMove = (moveEvent) => {
      nextHeight = Math.max(MIN_PAGE_MENU_LIST_HEIGHT, startHeight + (moveEvent.clientY - startY));
      setUserListHeight(nextHeight);
    };
    const onUp = () => {
      handle.removeEventListener("pointermove", onMove);
      handle.removeEventListener("pointerup", onUp);
      handle.removeEventListener("pointercancel", onUp);
      setIsResizing(false);
      try {
        window.localStorage.setItem(PAGE_MENU_LIST_HEIGHT_KEY, String(nextHeight));
      } catch {
      }
    };
    handle.addEventListener("pointermove", onMove);
    handle.addEventListener("pointerup", onUp, { once: true });
    handle.addEventListener("pointercancel", onUp, { once: true });
  }, []);
  const handleResizeDoubleClick = useCallback(() => {
    setUserListHeight(null);
    try {
      window.localStorage.removeItem(PAGE_MENU_LIST_HEIGHT_KEY);
    } catch {
    }
  }, []);
  useEffect(
    function closePageMenuOnEnterPressAfterPressingEnterToConfirmRename() {
      const doc = editor.getContainerDocument();
      function handleKeyDown2() {
        if (editingPageId) return;
        if (doc.activeElement === doc.body) {
          editor.menus.clearOpenMenus();
        }
      }
      doc.addEventListener("keydown", handleKeyDown2, { passive: true });
      return () => {
        doc.removeEventListener("keydown", handleKeyDown2);
      };
    },
    [editor, editingPageId]
  );
  const rMutables = useRef({
    status: "idle",
    id: null,
    startIndex: 0,
    dragIndex: 0,
    startY: 0,
    startScrollTop: 0,
    lastClientY: 0,
    // Set true on pointer-up after a drag, so the synthetic click that
    // follows pointer-up doesn't also navigate to the dragged page.
    justDragged: false,
    startedOnDragHandle: false,
    // Whether an auto-scroll rAF is in flight; the loop reschedules itself
    // while status === 'dragging' and exits otherwise.
    autoScrollScheduled: false
  });
  const [dragState, setDragState] = useState(null);
  useEffect(() => {
    if (!isOpen) return;
    editor.timers.requestAnimationFrame(() => {
      const container = rSortableContainer.current;
      if (!container) return;
      const currentIndex = editor.getPages().findIndex((p) => p.id === currentPage.id);
      if (currentIndex === -1) return;
      const doc = editor.getContainerDocument();
      const elm = doc.querySelector(`[data-pageid="${currentPage.id}"]`);
      elm?.querySelector("button.tlui-page-menu__item__button")?.focus();
      const elmTop = currentIndex * PAGE_MENU_ITEM_HEIGHT;
      const elmBottom = elmTop + PAGE_MENU_ITEM_HEIGHT;
      const viewTop = container.scrollTop;
      const viewBottom = viewTop + container.clientHeight;
      if (elmTop < viewTop) {
        container.scrollTo({ top: elmTop });
      } else if (elmBottom > viewBottom) {
        container.scrollTo({ top: elmBottom - container.clientHeight });
      }
    });
  }, [currentPage.id, isOpen, editor]);
  const updateDragFromPointer = useCallback(
    (clientY) => {
      const mut = rMutables.current;
      if (mut.status !== "dragging" || !mut.id) return;
      const scrollTop = rSortableContainer.current?.scrollTop ?? 0;
      const rawOffsetY = clientY - mut.startY + (scrollTop - mut.startScrollTop);
      const minDragY = 0;
      const maxDragY = (pages.length - 1) * PAGE_MENU_ITEM_HEIGHT;
      const dragY = Math.max(
        minDragY,
        Math.min(maxDragY, mut.startIndex * PAGE_MENU_ITEM_HEIGHT + rawOffsetY)
      );
      const offsetY = dragY - mut.startIndex * PAGE_MENU_ITEM_HEIGHT;
      const dragIndex = Math.max(
        0,
        Math.min(Math.round(dragY / PAGE_MENU_ITEM_HEIGHT), pages.length - 1)
      );
      mut.dragIndex = dragIndex;
      mut.lastClientY = clientY;
      setDragState({ id: mut.id, startIndex: mut.startIndex, dragIndex, offsetY });
    },
    [pages.length]
  );
  useEffect(() => {
    if (!isOpen) return;
    const container = rSortableContainer.current;
    if (!container) return;
    function onWheel(e) {
      if (rMutables.current.status !== "dragging") return;
      e.preventDefault();
    }
    container.addEventListener("wheel", onWheel, { passive: false });
    return () => container.removeEventListener("wheel", onWheel);
  }, [isOpen]);
  const tickAutoScrollDuringDrag = useCallback(() => {
    const mut = rMutables.current;
    const container = rSortableContainer.current;
    if (mut.status !== "dragging" || !container) {
      mut.autoScrollScheduled = false;
      return;
    }
    const rect = container.getBoundingClientRect();
    const fromTop = mut.lastClientY - rect.top;
    const fromBottom = rect.bottom - mut.lastClientY;
    const maxScroll = container.scrollHeight - container.clientHeight;
    const overshootTop = PAGE_MENU_AUTO_SCROLL_ZONE - fromTop;
    const overshootBottom = PAGE_MENU_AUTO_SCROLL_ZONE - fromBottom;
    let dy = 0;
    if (overshootTop > 0 && container.scrollTop > 0) {
      const t = Math.min(1, overshootTop / PAGE_MENU_AUTO_SCROLL_RAMP_DISTANCE);
      const speed = PAGE_MENU_MIN_AUTO_SCROLL_SPEED + (PAGE_MENU_MAX_AUTO_SCROLL_SPEED - PAGE_MENU_MIN_AUTO_SCROLL_SPEED) * t;
      dy = -Math.ceil(speed);
    } else if (overshootBottom > 0 && container.scrollTop < maxScroll) {
      const t = Math.min(1, overshootBottom / PAGE_MENU_AUTO_SCROLL_RAMP_DISTANCE);
      const speed = PAGE_MENU_MIN_AUTO_SCROLL_SPEED + (PAGE_MENU_MAX_AUTO_SCROLL_SPEED - PAGE_MENU_MIN_AUTO_SCROLL_SPEED) * t;
      dy = Math.ceil(speed);
    }
    if (dy !== 0) {
      const before = container.scrollTop;
      container.scrollTop = Math.max(0, Math.min(maxScroll, before + dy));
      if (container.scrollTop !== before) {
        updateDragFromPointer(mut.lastClientY);
      }
    }
    editor.timers.requestAnimationFrame(tickAutoScrollDuringDrag);
  }, [editor, updateDragFromPointer]);
  const ensureAutoScrollLoop = useCallback(() => {
    const mut = rMutables.current;
    if (mut.autoScrollScheduled) return;
    mut.autoScrollScheduled = true;
    editor.timers.requestAnimationFrame(tickAutoScrollDuringDrag);
  }, [editor, tickAutoScrollDuringDrag]);
  const handlePointerDown = useCallback((e) => {
    if (e.button !== 0) return;
    const { clientY, currentTarget } = e;
    const { id, index } = currentTarget.dataset;
    if (!id || index === void 0) return;
    const startedOnDragHandle = currentTarget.dataset.dragHandle === "true";
    if (startedOnDragHandle) {
      e.preventDefault();
      e.stopPropagation();
    }
    const mut = rMutables.current;
    setPointerCapture(currentTarget, e);
    mut.status = "pointing";
    mut.id = id;
    mut.startIndex = +index;
    mut.dragIndex = +index;
    mut.startY = clientY;
    mut.lastClientY = clientY;
    mut.startScrollTop = rSortableContainer.current?.scrollTop ?? 0;
    mut.startedOnDragHandle = startedOnDragHandle;
  }, []);
  const handlePointerMove = useCallback(
    (e) => {
      const mut = rMutables.current;
      const { clientY } = e;
      if (mut.status === "pointing") {
        if (Math.abs(clientY - mut.startY) <= PAGE_MENU_DRAG_THRESHOLD) return;
        mut.status = "dragging";
        mut.lastClientY = clientY;
        ensureAutoScrollLoop();
      }
      if (mut.status === "dragging") {
        e.preventDefault();
        updateDragFromPointer(clientY);
      }
    },
    [ensureAutoScrollLoop, updateDragFromPointer]
  );
  const handlePointerUp = useCallback(
    (e) => {
      const mut = rMutables.current;
      if (mut.status === "dragging" && mut.id) {
        onMovePage(editor, mut.id, mut.startIndex, mut.dragIndex, trackEvent);
        if (!mut.startedOnDragHandle) {
          mut.justDragged = true;
        }
      }
      releasePointerCapture(e.currentTarget, e);
      mut.status = "idle";
      mut.id = null;
      mut.startedOnDragHandle = false;
      setDragState(null);
    },
    [editor, trackEvent]
  );
  const handleItemContextMenu = useCallback(
    (e, index) => {
      e.preventDefault();
      e.stopPropagation();
      closePageItemSubmenus(index);
      editor.menus.addOpenMenu(`page item submenu ${index}`);
    },
    [closePageItemSubmenus, editor]
  );
  const handlePointerCancel = useCallback((e) => {
    const mut = rMutables.current;
    releasePointerCapture(e.currentTarget, e);
    mut.status = "idle";
    mut.id = null;
    mut.startedOnDragHandle = false;
    setDragState(null);
  }, []);
  const handleKeyDown = useCallback((e) => {
    const mut = rMutables.current;
    if (e.key === "Escape" && mut.status !== "idle") {
      mut.status = "idle";
      mut.id = null;
      mut.startedOnDragHandle = false;
      setDragState(null);
    }
  }, []);
  const shouldUseWindowPrompt = breakpoint < PORTRAIT_BREAKPOINT.TABLET_SM && isCoarsePointer;
  const shouldUseDragHandle = !isReadonlyMode && isCoarsePointer;
  const startRenamingPage = useCallback(
    (id, currentName) => {
      if (isReadonlyMode) return;
      if (shouldUseWindowPrompt) {
        const name = window.prompt(msg("action.rename"), currentName);
        if (name && name !== currentName) {
          editor.renamePage(id, name);
          trackEvent("rename-page", { source: "page-menu" });
        }
        return;
      }
      setEditingPageId(id);
    },
    [editor, msg, isReadonlyMode, shouldUseWindowPrompt, trackEvent]
  );
  const handleCreatePageClick = useCallback(() => {
    if (isReadonlyMode) return;
    const newPageId = PageRecordType.createId();
    const initialName = msg("page-menu.new-page-initial-name");
    let name = initialName;
    if (shouldUseWindowPrompt) {
      const result = window.prompt(msg("page-menu.create-new-page"), initialName);
      if (result === null) return;
      name = result || initialName;
    }
    editor.run(() => {
      editor.markHistoryStoppingPoint("creating page");
      editor.createPage({ name, id: newPageId });
      editor.setCurrentPage(newPageId);
    });
    if (!shouldUseWindowPrompt) {
      startRenamingPage(newPageId, initialName);
    }
    trackEvent("new-page", { source: "page-menu" });
  }, [editor, msg, isReadonlyMode, shouldUseWindowPrompt, startRenamingPage, trackEvent]);
  const changePage = useCallback(
    (id) => {
      editor.setCurrentPage(id);
      trackEvent("change-page", { source: "page-menu" });
    },
    [editor, trackEvent]
  );
  return /* @__PURE__ */ jsxs(TldrawUiPopover, { id: "pages", onOpenChange, open: isOpen, children: [
    /* @__PURE__ */ jsx(TldrawUiPopoverTrigger, { "data-testid": "main.page-menu", children: /* @__PURE__ */ jsxs(
      TldrawUiButton,
      {
        type: "menu",
        tooltip: currentPage.name,
        title: currentPage.name,
        "data-testid": "page-menu.button",
        className: "tlui-page-menu__trigger",
        children: [
          /* @__PURE__ */ jsx(TldrawUiButtonLabel, { children: currentPage.name }),
          /* @__PURE__ */ jsx(TldrawUiButtonIcon, { icon: "chevron-down", small: true })
        ]
      }
    ) }),
    /* @__PURE__ */ jsx(
      TldrawUiPopoverContent,
      {
        side: "bottom",
        align: "start",
        sideOffset: 0,
        disableEscapeKeyDown: editingPageId !== null,
        children: /* @__PURE__ */ jsxs("div", { className: "tlui-page-menu__wrapper", children: [
          /* @__PURE__ */ jsx(
            "div",
            {
              "data-testid": "page-menu.list",
              className: "tlui-page-menu__list",
              ref: rSortableContainer,
              style: { height: renderedListHeight },
              children: /* @__PURE__ */ jsx(
                "div",
                {
                  className: "tlui-page-menu__list__content",
                  "data-dragging": dragState !== null,
                  style: { height: autoFitListHeight },
                  children: pages.map((page, index) => {
                    const isCurrentPage = page.id === currentPage.id;
                    const isRenamingThisPage = editingPageId === page.id;
                    const isDragging = dragState?.id === page.id;
                    let y = index * PAGE_MENU_ITEM_HEIGHT;
                    if (dragState) {
                      if (isDragging) {
                        y = dragState.startIndex * PAGE_MENU_ITEM_HEIGHT + dragState.offsetY;
                      } else {
                        const { startIndex, dragIndex } = dragState;
                        if (dragIndex < startIndex && index >= dragIndex && index < startIndex) {
                          y = (index + 1) * PAGE_MENU_ITEM_HEIGHT;
                        } else if (dragIndex > startIndex && index > startIndex && index <= dragIndex) {
                          y = (index - 1) * PAGE_MENU_ITEM_HEIGHT;
                        }
                      }
                    }
                    return /* @__PURE__ */ jsxs(
                      "div",
                      {
                        "data-pageid": page.id,
                        "data-testid": "page-menu.item",
                        "data-iscurrent": isCurrentPage,
                        "data-dragging": isDragging,
                        "data-editing": isRenamingThisPage,
                        className: "tlui-page-menu__item",
                        onContextMenu: !isReadonlyMode && !isRenamingThisPage ? (e) => handleItemContextMenu(e, index) : void 0,
                        style: {
                          zIndex: isDragging ? pages.length + 2 : isCurrentPage ? pages.length + 1 : index,
                          transform: `translateY(${y}px)`
                        },
                        children: [
                          isRenamingThisPage ? /* @__PURE__ */ jsx("div", { className: "tlui-page-menu__item__title", children: /* @__PURE__ */ jsx(
                            PageItemInput,
                            {
                              id: page.id,
                              name: page.name,
                              isCurrentPage,
                              onComplete: () => setEditingPageId(null),
                              onCancel: () => setEditingPageId(null)
                            }
                          ) }) : /* @__PURE__ */ jsxs(Fragment, { children: [
                            shouldUseDragHandle && /* @__PURE__ */ jsx(
                              TldrawUiButton,
                              {
                                type: "icon",
                                className: "tlui-page-menu__item__drag-handle",
                                "data-testid": "page-menu.item-drag-handle",
                                "data-id": page.id,
                                "data-index": index,
                                "data-drag-handle": "true",
                                onPointerDown: handlePointerDown,
                                onPointerMove: handlePointerMove,
                                onPointerUp: handlePointerUp,
                                onPointerCancel: handlePointerCancel,
                                onKeyDown: handleKeyDown,
                                tooltip: msg("context-menu.reorder"),
                                title: msg("context-menu.reorder"),
                                children: /* @__PURE__ */ jsx(TldrawUiButtonIcon, { icon: "drag-handle-dots", small: true })
                              }
                            ),
                            /* @__PURE__ */ jsx(
                              TldrawUiButton,
                              {
                                type: "normal",
                                className: "tlui-page-menu__item__button",
                                onClick: () => {
                                  if (rMutables.current.justDragged) {
                                    rMutables.current.justDragged = false;
                                    return;
                                  }
                                  changePage(page.id);
                                },
                                onDoubleClick: () => startRenamingPage(page.id, page.name),
                                onPointerDown: isReadonlyMode || shouldUseDragHandle ? void 0 : handlePointerDown,
                                onPointerMove: isReadonlyMode || shouldUseDragHandle ? void 0 : handlePointerMove,
                                onPointerUp: isReadonlyMode || shouldUseDragHandle ? void 0 : handlePointerUp,
                                onPointerCancel: isReadonlyMode || shouldUseDragHandle ? void 0 : handlePointerCancel,
                                tooltip: msg("page-menu.go-to-page"),
                                title: msg("page-menu.go-to-page"),
                                "data-id": page.id,
                                "data-index": index,
                                onKeyDown: (e) => {
                                  if (e.key === "Escape") {
                                    handleKeyDown(e);
                                    return;
                                  }
                                  if (e.key === "Enter" && isCurrentPage) {
                                    startRenamingPage(page.id, page.name);
                                    editor.markEventAsHandled(e);
                                  }
                                },
                                children: /* @__PURE__ */ jsx(TldrawUiButtonLabel, { children: page.name })
                              }
                            )
                          ] }),
                          !isReadonlyMode && !isRenamingThisPage && /* @__PURE__ */ jsx("div", { className: "tlui-page-menu__item__submenu", children: /* @__PURE__ */ jsx(
                            PageItemSubmenu,
                            {
                              index,
                              item: page,
                              listSize: pages.length,
                              onRename: () => startRenamingPage(page.id, page.name)
                            }
                          ) })
                        ]
                      },
                      page.id
                    );
                  })
                }
              )
            }
          ),
          /* @__PURE__ */ jsx(
            "div",
            {
              className: "tlui-page-menu__resize-handle",
              "data-resizing": isResizing,
              onPointerDown: handleResizePointerDown,
              onDoubleClick: handleResizeDoubleClick,
              role: "separator",
              "aria-orientation": "horizontal",
              "aria-label": msg("page-menu.resize")
            }
          ),
          /* @__PURE__ */ jsxs(
            TldrawUiButton,
            {
              type: "menu",
              className: "tlui-page-menu__create-button",
              "data-testid": "page-menu.create",
              tooltip: createPageButtonLabel,
              title: createPageButtonLabel,
              disabled: isReadonlyMode || hasReachedMaxPages,
              onClick: handleCreatePageClick,
              children: [
                /* @__PURE__ */ jsx(TldrawUiButtonLabel, { children: createPageButtonLabel }),
                !hasReachedMaxPages && /* @__PURE__ */ jsx(TldrawUiButtonIcon, { icon: "plus", small: true })
              ]
            }
          )
        ] })
      }
    )
  ] });
});
export {
  DefaultPageMenu
};
//# sourceMappingURL=DefaultPageMenu.mjs.map
